import { useCallback, useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, Copy, CreditCard, Lock, QrCode, Smartphone, XCircle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  api,
  type ApiError,
  type OrderStatus,
  type PayQr,
  type PaymentMethod,
  orderQrSvgUrl,
} from '../lib/api';
import { copyText } from '../lib/clipboard';
import { formatPrice } from '../lib/format';
import { useOrder, usePay } from '../lib/queries';

const IS_DEV = import.meta.env.DEV;

const DONE: OrderStatus[] = ['PAID', 'DELIVERED'];
const TERMINAL: OrderStatus[] = ['EXPIRED', 'CANCELLED', 'REFUNDED'];

function CopyRow({ label, value, display }: { label: string; value: string; display: string }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-line bg-card px-3.5 py-2.5">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-soft">{label}</p>
        <p className="truncate font-mono text-sm font-semibold text-ink">{display}</p>
      </div>
      <button
        type="button"
        onClick={() => copyText(value, label)}
        disabled={!value}
        aria-label={`Kopírovat: ${label}`}
        className="grid h-11 w-11 flex-none place-items-center rounded-lg text-board transition hover:bg-paper2 disabled:opacity-40"
      >
        <Copy className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  );
}

function QrPanel({ token, pay }: { token: string; pay: PayQr | null }) {
  return (
    <div className="px-4 pb-5 sm:flex sm:gap-5">
      {/* QR kód */}
      <div className="mx-auto w-fit rounded-2xl border border-line bg-card p-3 shadow-card sm:mx-0">
        {pay?.qr_svg ? (
          <div
            className="h-44 w-44 [&>svg]:h-full [&>svg]:w-full"
            role="img"
            aria-label={`QR kód pro platbu ${formatPrice(pay.amount_czk)}`}
            dangerouslySetInnerHTML={{ __html: pay.qr_svg }}
          />
        ) : (
          <img
            src={orderQrSvgUrl(token)}
            alt="QR kód pro platbu"
            className="h-44 w-44"
            width={176}
            height={176}
          />
        )}
        <p className="mt-1 text-center text-[11px] font-semibold text-ink-soft">
          QR Platba{pay ? ` · ${formatPrice(pay.amount_czk)}` : ''}
        </p>
      </div>

      {/* Údaje k ručnímu převodu */}
      <div className="mt-4 grow space-y-2.5 sm:mt-0">
        <CopyRow label="Číslo účtu" value={pay?.account ?? ''} display={pay?.account ?? '…'} />
        <CopyRow
          label="Variabilní symbol"
          value={pay?.variable_symbol ?? ''}
          display={pay?.variable_symbol ?? '…'}
        />
        <CopyRow
          label="Částka"
          value={pay ? String(pay.amount_czk) : ''}
          display={pay ? formatPrice(pay.amount_czk) : '…'}
        />
        {pay?.spayd && (
          <a
            href={pay.spayd}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-board/40 bg-card font-semibold text-board transition hover:bg-paper2 active:scale-[.98]"
          >
            <Smartphone className="h-5 w-5" strokeWidth={2} />
            Otevřít v bankovní aplikaci
          </a>
        )}
      </div>
    </div>
  );
}

function PaymentSkeleton() {
  return (
    <div className="mx-auto max-w-2xl space-y-4 px-4 py-6">
      <div className="h-24 rounded-card bg-paper2 motion-safe:animate-pulse" />
      <div className="h-64 rounded-card bg-paper2 motion-safe:animate-pulse" />
      <div className="h-20 rounded-card bg-paper2 motion-safe:animate-pulse" />
    </div>
  );
}

export default function Payment() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  // Žádná metoda není předvybraná: kupující musí QR nebo kartu aktivně zvolit,
  // teprve pak se platba připraví (žádné automatické založení QR po otevření).
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [qr, setQr] = useState<PayQr | null>(null);

  const pay = usePay(token);

  const {
    data: order,
    isLoading,
    isError,
    refetch,
  } = useOrder(token, {
    poll: true,
  });

  const orderStatus = order?.status;
  const isTerminal = orderStatus ? TERMINAL.includes(orderStatus) : false;

  // Po zaplacení / doručení → potvrzovací stránka.
  useEffect(() => {
    if (orderStatus && DONE.includes(orderStatus) && token) {
      navigate(`/objednavka/${token}`, { replace: true });
    }
  }, [orderStatus, token, navigate]);

  const requestQr = useCallback(() => {
    if (!token) return;
    pay.mutate('QR', {
      onSuccess: (res) => {
        if (res.method === 'QR') setQr(res);
      },
      onError: (err) =>
        toast.error('QR platbu se nepodařilo připravit', {
          description: (err as ApiError)?.message,
        }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const chooseQr = () => {
    setMethod('QR');
    if (!qr && !pay.isPending) {
      requestQr();
    }
  };

  const devMarkPaid = async () => {
    if (!token) return;
    try {
      await api.devMarkPaid(token);
      toast.success('Dev: označeno jako zaplaceno');
      refetch();
    } catch (err) {
      toast.error('Dev: nepodařilo se označit', {
        description: (err as ApiError)?.message,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="grid-paper min-h-[70vh]">
        <PaymentSkeleton />
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="grid-paper min-h-[70vh]">
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <h1 className="text-3xl">Objednávka nenalezena</h1>
          <p className="mt-3 text-ink-soft">
            Odkaz na platbu je neplatný nebo vypršel. Zkuste objednávku vytvořit znovu.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex h-12 items-center rounded-xl bg-board px-6 font-semibold text-chalk transition hover:bg-board-deep"
          >
            Zpět do katalogu
          </Link>
        </div>
      </div>
    );
  }

  const entrance = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.25 },
      };

  return (
    <div className="grid-paper min-h-[70vh] pb-16">
      <motion.main className="mx-auto max-w-2xl px-4 py-5" {...entrance}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <Link
            to={`/produkt/${order.product.slug}`}
            className="inline-flex h-11 items-center gap-1.5 rounded-xl px-2 text-sm font-medium text-ink-soft transition hover:text-ink"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
            Zpět na produkt
          </Link>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-soft">
            <Lock className="h-3.5 w-3.5" strokeWidth={2} />
            Zabezpečený nákup
          </span>
        </div>

        {/* Souhrn */}
        <section className="flex items-center gap-4 rounded-card border border-line bg-card p-4 shadow-card">
          <div className="min-w-0 grow">
            <p className="font-semibold leading-snug">{order.product.title}</p>
            <p className="mt-0.5 text-sm tabular-nums text-ink-soft">
              Obj. VS {order.variable_symbol}
            </p>
          </div>
          <p className="shrink-0 text-xl font-bold tabular-nums text-ink">
            {formatPrice(order.amount_czk)}
          </p>
        </section>

        {isTerminal ? (
          <section className="mt-6 rounded-card border border-error/30 bg-error/5 p-5 text-center">
            <XCircle className="mx-auto h-10 w-10 text-error" strokeWidth={2} />
            <h2 className="mt-3 text-xl">
              {orderStatus === 'EXPIRED'
                ? 'Platnost objednávky vypršela'
                : 'Objednávka byla zrušena'}
            </h2>
            <p className="mt-2 text-sm text-ink-soft">
              Vytvořte prosím novou objednávku, rádi vám materiál doručíme.
            </p>
            <Link
              to={`/pokladna/nova?produkt=${encodeURIComponent(order.product.slug)}`}
              className="mt-5 inline-flex h-12 items-center rounded-xl bg-board px-6 font-semibold text-chalk transition hover:bg-board-deep"
            >
              Zkusit znovu
            </Link>
          </section>
        ) : (
          <section className="mt-6">
            <h2 className="text-xl">Jak chcete zaplatit?</h2>
            <div className="mt-3 space-y-3">
              {/* QR karta */}
              <div
                className={`rounded-card border-2 transition ${
                  method === 'QR' ? 'border-board bg-paper2/60 shadow-card' : 'border-line bg-card'
                }`}
              >
                <button
                  type="button"
                  onClick={chooseQr}
                  className="flex w-full items-start gap-3 p-4 text-left"
                >
                  <span
                    className={`mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full border-2 ${
                      method === 'QR' ? 'border-board' : 'border-line'
                    }`}
                  >
                    {method === 'QR' && <span className="h-2.5 w-2.5 rounded-full bg-board" />}
                  </span>
                  <span className="grid h-11 w-11 flex-none place-items-center rounded-xl border border-line bg-card text-board">
                    <QrCode className="h-6 w-6" strokeWidth={2} />
                  </span>
                  <span className="grow">
                    <span className="flex flex-wrap items-center gap-2 font-bold text-ink">
                      QR platba z bankovní aplikace
                      <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-bold text-ink">
                        Doporučujeme · bez poplatku
                      </span>
                    </span>
                    <span className="mt-0.5 block text-sm text-ink-soft">
                      Připsání obvykle do minuty
                    </span>
                  </span>
                </button>

                {method === 'QR' && (
                  <>
                    {pay.isPending && !qr ? (
                      <div className="px-4 pb-5">
                        <div className="h-44 rounded-2xl bg-paper2 motion-safe:animate-pulse" />
                      </div>
                    ) : (
                      <QrPanel token={token as string} pay={qr} />
                    )}

                    {/* Živý stav */}
                    <div className="mx-4 mb-4 flex items-center gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3">
                      <span className="h-2.5 w-2.5 flex-none rounded-full bg-warning motion-safe:animate-pulse" />
                      <div className="text-sm">
                        <p className="font-semibold text-warning">Čekáme na platbu…</p>
                        <p className="text-ink-soft">
                          Po odeslání ji spárujeme automaticky, obvykle do minuty. Stránku nemusíte
                          obnovovat.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Karta (Comgate) je zatím v přípravě, hlavní platba je QR kód. */}
              <div className="rounded-card border-2 border-line bg-card opacity-70">
                <div className="flex w-full items-start gap-3 p-4 text-left">
                  <span className="mt-0.5 grid h-5 w-5 flex-none rounded-full border-2 border-line" />
                  <span className="grid h-11 w-11 flex-none place-items-center rounded-xl border border-line bg-paper2 text-ink-soft">
                    <CreditCard className="h-6 w-6" strokeWidth={2} />
                  </span>
                  <span className="grow">
                    <span className="flex flex-wrap items-center gap-2 font-bold text-ink">
                      Platební karta online
                      <span className="rounded-full bg-paper2 px-2 py-0.5 text-[11px] font-bold text-ink-soft ring-1 ring-line">
                        Připravujeme
                      </span>
                    </span>
                    <span className="mt-0.5 block text-sm text-ink-soft">
                      Platbu kartou přes Comgate spustíme brzy. Teď prosím využijte QR platbu výše.
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Dev pomůcka i pro QR tok */}
            {IS_DEV && (
              <button
                type="button"
                onClick={devMarkPaid}
                className="mt-4 inline-flex h-10 items-center gap-2 rounded-xl border border-dashed border-line px-3 text-xs font-semibold text-ink-soft transition hover:border-board hover:text-board"
              >
                Dev: označit zaplaceno (simulace platby)
              </button>
            )}
          </section>
        )}
      </motion.main>
    </div>
  );
}
